import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import {
  ResourceEmployeeProfile,
  ResourceEmployeeProfileUpdate,
  ResourceTeam,
  ResourceTeamSave,
  ResourceUserOption
} from '../../../Models/resource-modules.models';
import { ResourceKnowledge } from '../../../Models/resource.models';
import { ResourceAccessService, ResourcePlanningAccess } from '../../../Services/resource-access.service';
import { ResourceTeamApiService } from '../../../Services/resource-modules-api.service';
import { ResourceApiService } from '../../../Services/resource-api.service';

/**
 * Gemeinsamer Zustand der Team- und Mitarbeiterverwaltung (pro Seitenaufruf).
 *
 * Alle Änderungen an Teammitgliedschaften, Teamleitung und Hauptteam laufen über
 * diese eine Stelle und dieselben API-Endpunkte – egal ob sie aus der Teamansicht
 * oder aus dem Mitarbeiterprofil ausgelöst werden. Die Rechte-Abfragen hier steuern
 * nur die Oberfläche; das Backend prüft jede Aktion selbst.
 */
@Injectable()
export class ResourceTeamManagementStore {
  access: ResourcePlanningAccess | null = null;
  teams: ResourceTeam[] = [];
  /** Sichtbare Mitarbeiterprofile (global alle Konten, Teamleitung: eigene Teammitglieder). */
  profiles: ResourceEmployeeProfile[] = [];
  skills: ResourceKnowledge[] = [];
  /** Benutzerauswahl für Teammitglieder (nur für Teamanlage/-verwaltung). */
  userOptions: ResourceUserOption[] = [];

  loading = false;
  busy = false;
  error = '';

  private profileById = new Map<string, ResourceEmployeeProfile>();

  constructor(
    private accessApi: ResourceAccessService,
    private teamApi: ResourceTeamApiService,
    private resourceApi: ResourceApiService
  ) { }

  // ---- Rechte (nur Oberfläche) ---------------------------------------------------

  get currentUserId(): string { return this.access?.userId ?? ''; }
  get isGlobal(): boolean { return !!this.access?.isGlobalPlanner; }
  get canCreateTeam(): boolean { return !!this.access?.canCreateTeam; }
  get ledTeamIds(): number[] { return (this.access?.teams ?? []).filter(t => t.isLeader).map(t => t.teamId); }
  get leadsAnyTeam(): boolean { return this.ledTeamIds.length > 0; }

  /** „Alle Mitarbeiter“: globale Planer und Teamleitungen (diese nur ihre Teammitglieder). */
  get canSeeDirectory(): boolean { return this.isGlobal || this.leadsAnyTeam; }

  /** Wochenkapazität und alle Skills: SuperAdmin und Kaufmännisch. */
  get canEditProfiles(): boolean { return this.isGlobal; }

  /** Mitarbeiter manuell in die Gesamtplanung aufnehmen bzw. daraus entfernen: SuperAdmin und Kaufmännisch. */
  get canManagePlanningMembers(): boolean { return this.isGlobal; }

  canManageTeam(team: ResourceTeam | null | undefined): boolean {
    return !!team && (this.isGlobal || team.members.some(m => m.userId === this.currentUserId && m.isLeader));
  }

  /** Teams, in die der Benutzer Mitarbeiter aufnehmen bzw. aus denen er sie entfernen darf. */
  get manageableTeams(): ResourceTeam[] {
    return this.teams.filter(t => t.isActive && this.canManageTeam(t));
  }

  /** Skills eines Mitglieds pflegen: global alle, Teamleitung für Mitglieder ihrer Teams. */
  canEditSkillsOf(userId: string): boolean {
    if (this.isGlobal) return true;
    return this.teams.some(t => this.ledTeamIds.includes(t.id) && t.members.some(m => m.userId === userId));
  }

  /** Skills, die der Benutzer zuordnen darf (Teamleitung: global + eigene Teamkataloge). */
  get assignableSkills(): ResourceKnowledge[] {
    if (this.isGlobal) return this.skills;
    const led = new Set(this.ledTeamIds);
    return this.skills.filter(s => !s.ownerTeamId || led.has(s.ownerTeamId));
  }

  /** Hauptteam für sich selbst immer, für andere nur SuperAdmin/Kaufmännisch. */
  canSetPrimaryFor(userId: string): boolean {
    return this.isGlobal || userId === this.currentUserId;
  }

  // ---- Lesen -------------------------------------------------------------------------

  profile(userId: string): ResourceEmployeeProfile | undefined {
    return this.profileById.get(userId);
  }

  team(teamId: number | null | undefined): ResourceTeam | undefined {
    return this.teams.find(t => t.id === teamId);
  }

  skillNames(skillIds: number[]): string[] {
    return this.skills.filter(s => skillIds.includes(s.id)).map(s => s.name);
  }

  // ---- Laden -------------------------------------------------------------------------

  load(): Observable<void> {
    this.loading = true;
    this.error = '';
    return this.accessApi.getMine().pipe(
      tap(access => this.access = access),
      switchMap(() => forkJoin({
        teams: this.teamApi.getTeams(true),
        profiles: this.canSeeDirectory
          ? this.teamApi.getEmployeeProfiles(false)
          : of([] as ResourceEmployeeProfile[]),
        skills: this.resourceApi.getKnowledge().pipe(catchError(() => of([] as ResourceKnowledge[]))),
        users: this.isGlobal || this.canCreateTeam || this.leadsAnyTeam
          ? this.teamApi.getUserOptions().pipe(catchError(() => of([] as ResourceUserOption[])))
          : of([] as ResourceUserOption[])
      })),
      tap(result => {
        this.teams = result.teams;
        this.setProfiles(result.profiles);
        this.skills = result.skills;
        this.userOptions = result.users;
        this.loading = false;
      }),
      map(() => undefined),
      catchError((err: HttpErrorResponse) => {
        this.loading = false;
        this.error = ResourceTeamManagementStore.errorText(err, 'Teams und Mitarbeiter konnten nicht geladen werden.');
        return of(undefined);
      })
    );
  }

  /** Teams, eigene Rechte und Profile neu laden (nach jeder Änderung). */
  private refresh(): Observable<void> {
    return forkJoin({
      access: this.accessApi.getMine(),
      teams: this.teamApi.getTeams(true)
    }).pipe(
      tap(result => { this.access = result.access; this.teams = result.teams; }),
      switchMap(() => this.canSeeDirectory
        ? this.teamApi.getEmployeeProfiles(false).pipe(catchError(() => of(this.profiles)))
        : of([] as ResourceEmployeeProfile[])),
      tap(profiles => this.setProfiles(profiles)),
      map(() => undefined)
    );
  }

  private setProfiles(profiles: ResourceEmployeeProfile[]): void {
    this.profiles = [...profiles].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    this.profileById = new Map(this.profiles.map(p => [p.userId, p]));
  }

  // ---- Teams ---------------------------------------------------------------------------

  createTeam(payload: ResourceTeamSave): Observable<ResourceTeam> {
    return this.run(this.teamApi.createTeam(payload));
  }

  updateTeam(teamId: number, payload: ResourceTeamSave): Observable<ResourceTeam> {
    return this.run(this.teamApi.updateTeam(teamId, payload));
  }

  deleteTeam(teamId: number): Observable<void> {
    return this.run(this.teamApi.deleteTeam(teamId));
  }

  // ---- Mitgliedschaften (eine Implementierung für Team- und Profilansicht) ------------

  addMembers(teamId: number, userIds: string[]): Observable<ResourceTeam> {
    return this.run(this.teamApi.addMembers(teamId, userIds));
  }

  removeMember(teamId: number, userId: string): Observable<ResourceTeam> {
    return this.run(this.teamApi.removeMember(teamId, userId));
  }

  changeLeader(teamId: number, userId: string): Observable<ResourceTeam> {
    return this.run(this.teamApi.changeLeader(teamId, userId));
  }

  setPrimaryTeam(teamId: number, userId: string): Observable<unknown> {
    const forOther = userId !== this.currentUserId ? userId : undefined;
    return this.run(this.teamApi.setPrimaryTeam(teamId, forOther));
  }

  // ---- Profile -------------------------------------------------------------------------

  saveProfile(userId: string, payload: ResourceEmployeeProfileUpdate): Observable<ResourceEmployeeProfile> {
    // Teamzuordnung nie über das Profil-Speichern, sondern über die Mitglieder-Endpunkte.
    return this.run(this.teamApi.updateEmployeeProfile(userId, { ...payload, teamIds: null }));
  }

  // ---- Gesamtplanung: manuelle Aufnahme unabhängig von Teams --------------------------

  /** Bestehende Benutzerkonten zur Gesamtplanung hinzufügen (legt keine Konten an). */
  addToPlanning(userIds: string[]): Observable<ResourceEmployeeProfile[]> {
    return this.run(this.teamApi.addToPlanning(userIds));
  }

  /** Manuelle Aufnahme aufheben; Teammitglieder bleiben über ihre Teams in der Gesamtplanung. */
  removeFromPlanning(userId: string): Observable<ResourceEmployeeProfile> {
    return this.run(this.teamApi.removeFromPlanning(userId));
  }

  /** Benutzerkonten, die noch nicht manuell aufgenommen sind (Auswahl für „Zur Gesamtplanung hinzufügen“). */
  get planningCandidates(): ResourceUserOption[] {
    return this.userOptions.filter(u => !this.profile(u.userId)?.isManuallyPlanned);
  }

  // ---- Hilfen --------------------------------------------------------------------------

  /** Führt eine Änderung aus und lädt danach Teams, Rechte und Profile neu. */
  private run<T>(request: Observable<T>): Observable<T> {
    this.busy = true;
    return request.pipe(
      switchMap(result => this.refresh().pipe(
        catchError(() => of(undefined)),
        map(() => result))),
      tap({
        next: () => this.busy = false,
        error: () => this.busy = false
      })
    );
  }

  static errorText(error: HttpErrorResponse, fallback: string): string {
    if (typeof error?.error === 'string' && error.error.trim()) return error.error;
    if (error?.status === 403 || error?.status === 404) return 'Keine Berechtigung für diese Aktion.';
    if (error?.status === 0) return 'Server nicht erreichbar.';
    return error?.error?.message || fallback;
  }
}
